import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  FileText,
  Lock,
  LockOpen,
  Download,
  Calendar,
  Search,
  XCircle,
  LogOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuthStore } from '@/store/auth.store';
import { useVaults, useVaultDocuments, useVault } from '@/hooks/useVault';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import { VaultDocument, DocumentType } from '@/types/vault.types';
import {
  decryptDocumentComplete,
  decryptDocumentWithRSA,
} from '@/utils/crypto/vaultCrypto';
import { APP_CONFIG } from '@/constants/app';
import {
  UnlockVaultModal,
  TwoFactorRequiredPrompt,
  VaultUserLayout,
} from '../components';

/**
 * Simplified vault page for employees
 * Provides easy access to documents without the complexity of the admin interface
 */
export const EmployeeVaultPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [requires2fa, setRequires2fa] = useState(false);

  // Fetch user's vaults
  const { data: vaultsResponse, isLoading: vaultsLoading } = useVaults();
  const vaults = vaultsResponse || [];

  // Get the first vault (employees typically have one vault)
  const vault = vaults?.[0];

  // Fetch documents if vault exists
  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = useVaultDocuments(vault?.uuid || '', !!vault?.uuid, undefined);

  // Fetch full vault data (including encryptedPrivateKey for RSA decryption)
  const { data: vaultData } = useVault(vault?.uuid || '', !!vault?.uuid);

  // Vault key management
  const isVaultUnlocked = useVaultKeyStore(state => state.isVaultUnlocked);
  const getMasterKey = useVaultKeyStore(state => state.getMasterKey);
  const lockVault = useVaultKeyStore(state => state.lockVault);

  const isUnlocked = vault ? isVaultUnlocked(vault.uuid) : false;

  // Check for 2FA requirement error
  const is2faRequired =
    (documentsError as HttpApiError)?.response?.data?.error ===
      'TWO_FACTOR_REQUIRED' ||
    (documentsError as HttpApiError)?.response?.data?.twoFactorRequired === true;

  useEffect(() => {
    if (is2faRequired) {
      setRequires2fa(true);
    }
  }, [is2faRequired]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDownload = async (document: VaultDocument) => {
    if (!vault || !isUnlocked) {
      toast.error(
        t('vault.messages.vaultLocked', 'Veuillez déverrouiller le coffre')
      );
      return;
    }

    const masterKey = getMasterKey(vault.uuid);
    if (!masterKey) {
      toast.error(
        t('vault.messages.noVaultKey', 'Clé du coffre non disponible')
      );
      return;
    }

    setDownloadingId(document.uuid);

    try {
      const downloadUrl = `${APP_CONFIG.api.baseUrl}/vaults/${vault.uuid}/documents/${document.uuid}/download`;

      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          if (
            errorJson.error === 'TWO_FACTOR_REQUIRED' ||
            errorJson.twoFactorRequired
          ) {
            setRequires2fa(true);
            toast.error(
              t(
                'vault.messages.twoFactorRequired',
                "L'authentification à deux facteurs est requise"
              )
            );
            return;
          }
        } catch {
          // Not JSON
        }
        throw new Error(`Download failed: ${response.status}`);
      }

      const encryptedDocumentKey = response.headers.get('X-Encryption-Key');
      const iv = response.headers.get('X-Encryption-IV');
      const authTag = response.headers.get('X-Encryption-Auth-Tag');
      const documentKeyIv = response.headers.get('X-Document-Key-IV');
      const documentKeyAuthTag = response.headers.get(
        'X-Document-Key-Auth-Tag'
      );
      const originalFilename =
        response.headers.get('X-Original-Filename') ||
        document.originalFilename;

      if (!encryptedDocumentKey || !iv || !authTag) {
        throw new Error('Missing encryption metadata');
      }

      const encryptedContent = await response.arrayBuffer();

      // Detect encryption method: RSA (sync) vs AES-GCM (client upload)
      const isRSAEncrypted = !documentKeyIv || !documentKeyAuthTag;

      console.log('[EmployeeVaultPage] Download debug:', {
        isRSAEncrypted,
        documentKeyIv,
        documentKeyAuthTag,
        hasEncryptedPrivateKey: !!vaultData?.encryptedPrivateKey,
        encryptedContentSize: encryptedContent.byteLength,
        encryptedDocumentKeyLength: encryptedDocumentKey?.length,
      });

      let decrypted: ArrayBuffer;

      if (isRSAEncrypted) {
        // Document encrypted via server-side sync (RSA-encrypted DEK)
        if (!vaultData?.encryptedPrivateKey) {
          console.error(
            '[EmployeeVaultPage] RSA private key not available for decryption'
          );
          toast.error(
            t(
              'vault.messages.migrationRequired',
              'Ce document nécessite une migration du coffre. Veuillez verrouiller puis déverrouiller le coffre pour lancer la migration.'
            )
          );
          throw new Error('Vault RSA private key not available');
        }

        console.log('[EmployeeVaultPage] Decrypting with RSA...');
        decrypted = await decryptDocumentWithRSA(
          encryptedContent,
          encryptedDocumentKey,
          iv,
          authTag,
          vaultData.encryptedPrivateKey,
          masterKey
        );
        console.log(
          '[EmployeeVaultPage] RSA decryption completed, size:',
          decrypted.byteLength
        );
      } else {
        // Document encrypted via client-side upload (AES-GCM-encrypted DEK)
        console.log('[EmployeeVaultPage] Decrypting with AES-GCM...');
        decrypted = await decryptDocumentComplete(
          encryptedContent,
          encryptedDocumentKey,
          iv,
          authTag,
          documentKeyIv,
          documentKeyAuthTag,
          masterKey
        );
        console.log(
          '[EmployeeVaultPage] AES-GCM decryption completed, size:',
          decrypted.byteLength
        );
      }

      const blob = new Blob([decrypted], {
        type: document.mimeType || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = originalFilename;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);

      toast.success(t('vault.messages.downloadSuccess', 'Document téléchargé'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(
        t('vault.messages.downloadError', 'Erreur lors du téléchargement')
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === 'fr' ? 'fr-FR' : 'en-US',
      {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return i18n.language === 'fr' ? '0 Ko' : '0 KB';
    const k = 1024;
    const sizes =
      i18n.language === 'fr'
        ? ['o', 'Ko', 'Mo', 'Go']
        : ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getDocumentIcon = (type: DocumentType) => {
    switch (type) {
      case DocumentType.PAYSLIP:
        return <FileText className="text-primary-600 h-8 w-8" />;
      default:
        return <FileText className="h-8 w-8 text-neutral-500" />;
    }
  };

  const filteredDocuments = (documents || []).filter((doc: VaultDocument) =>
    doc.originalFilename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (vaultsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="text-primary-600 h-10 w-10 animate-spin" />
          <p className="text-sm text-neutral-600">
            {t('vault.myVault.loading')}
          </p>
        </div>
      </div>
    );
  }

  // 2FA required state
  if (requires2fa || is2faRequired) {
    return (
      <VaultUserLayout title={t('vault.myVault.title')}>
        <div className="flex flex-1 items-center justify-center">
          <TwoFactorRequiredPrompt
            onSetup={() => navigate('/settings?tab=security')}
          />
        </div>
      </VaultUserLayout>
    );
  }

  // No vault state
  if (!vault) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
              <XCircle className="h-6 w-6 text-neutral-500" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.myVault.noVault.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {t('vault.myVault.noVault.description')}
            </p>
            <button
              onClick={handleLogout}
              className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              {t('vault.myVault.logout')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <VaultUserLayout
      title={t('vault.myVault.title')}
      subtitle={t('vault.myVault.welcome', {
        name: user?.firstName || user?.email,
      })}
      showSettings
    >
      {/* Vault Status Card */}
      <div className="mb-6 rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-full ${isUnlocked ? 'bg-success-50' : 'bg-amber-50'}`}
            >
              {isUnlocked ? (
                <LockOpen className="text-success-600 h-5 w-5" />
              ) : (
                <Lock className="h-5 w-5 text-amber-600" />
              )}
            </div>
            <div>
              <h2 className="font-semibold text-neutral-900">{vault.name}</h2>
              <p className="text-sm text-neutral-500">
                {isUnlocked
                  ? t('vault.myVault.stats.unlocked')
                  : t('vault.myVault.stats.locked')}
              </p>
            </div>
          </div>
          {isUnlocked ? (
            <button
              onClick={() => lockVault(vault.uuid)}
              className="flex items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <Lock className="h-4 w-4" />
              {t('vault.myVault.actions.lock')}
            </button>
          ) : (
            <button
              onClick={() => setShowUnlockModal(true)}
              className="bg-primary-600 hover:bg-primary-700 flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            >
              <LockOpen className="h-4 w-4" />
              {t('vault.myVault.actions.unlock')}
            </button>
          )}
        </div>
      </div>

      {/* Documents Section */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        {/* Search */}
        <div className="border-b border-neutral-200 p-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder={t('vault.myVault.documents.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-neutral-300 py-2 pr-4 pl-9 text-sm text-neutral-900 placeholder-neutral-400 focus:ring-1 focus:outline-none"
            />
          </div>
        </div>

        {/* Documents List */}
        <div className="divide-y divide-neutral-100">
          {documentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
            </div>
          ) : !isUnlocked ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
                <Lock className="h-6 w-6 text-neutral-400" />
              </div>
              <h3 className="mb-2 text-base font-medium text-neutral-900">
                {t('vault.myVault.documents.vaultLocked')}
              </h3>
              <p className="mb-4 text-sm text-neutral-500">
                {t('vault.myVault.documents.unlockToView')}
              </p>
              <button
                onClick={() => setShowUnlockModal(true)}
                className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                <LockOpen className="h-4 w-4" />
                {t('vault.myVault.actions.unlock')}
              </button>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
                <FileText className="h-6 w-6 text-neutral-400" />
              </div>
              <h3 className="mb-2 text-base font-medium text-neutral-900">
                {searchQuery
                  ? t('vault.myVault.documents.noResults')
                  : t('vault.myVault.documents.empty')}
              </h3>
              <p className="text-sm text-neutral-500">
                {searchQuery
                  ? t('vault.myVault.documents.noResultsDescription')
                  : t('vault.myVault.documents.emptyDescription')}
              </p>
            </div>
          ) : (
            filteredDocuments.map((doc: VaultDocument) => (
              <div
                key={doc.uuid}
                className="flex items-center gap-4 p-4 transition-colors hover:bg-neutral-50"
              >
                <div className="bg-primary-50 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                  {getDocumentIcon(doc.type)}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-sm font-medium text-neutral-900">
                    {doc.originalFilename}
                  </h4>
                  <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-neutral-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(doc.createdAt)}
                    </span>
                    <span>{formatFileSize(doc.originalSize)}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.uuid}
                  className="border-primary-200 bg-primary-50 text-primary-600 hover:bg-primary-100 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border transition-colors disabled:opacity-50"
                >
                  {downloadingId === doc.uuid ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Security Note */}
      <div className="mt-6 rounded-xl border border-neutral-200 bg-white px-6 py-4">
        <div className="flex items-start gap-3 text-xs text-neutral-600">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
          <p>{t('vault.myVault.security.message')}</p>
        </div>
      </div>

      {/* Unlock Modal */}
      {vault && (
        <UnlockVaultModal
          isOpen={showUnlockModal}
          onClose={() => setShowUnlockModal(false)}
          vaultId={vault.uuid}
        />
      )}
    </VaultUserLayout>
  );
};
